import {
  AccountingFieldMatch,
  FieldMappingBaseFieldType,
  FieldMappingBatchUpsertPayloadMappingFieldType,
  FieldMappingBatchUpsertPayloadType,
  FieldMappingCreateReqType,
  FieldMappingObjectType,
  FieldMappingsResType,
  FieldMappingSystemType,
  FieldMappingType,
} from '@monetize/types/app';
import {
  convertToField,
  stringifyDisplayLabel,
  transformToDisplayLabelLookupRecord,
} from './crmFieldMapping';

interface MapMatchingNetsuiteFields {
  creditToCreditMemo?: AccountingFieldMatch;
  invoiceToInvoice?: AccountingFieldMatch;
}
export const mapMatchingNetsuiteFields = (
  accountingFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
  monetizeNowAccountingFields?: FieldMappingType,
): MapMatchingNetsuiteFields => {
  if (!accountingFields || !monetizeNowFields || !monetizeNowAccountingFields) {
    return {};
  }
  let response: Partial<MapMatchingNetsuiteFields> = {};
  if (
    'CreditMemo' in accountingFields &&
    ('CreditNote' in monetizeNowFields ||
      'CreditNote' in monetizeNowAccountingFields)
  ) {
    response = {
      ...response,
      creditToCreditMemo: {
        accounting: accountingFields.CreditMemo || [],
        monetize: (monetizeNowAccountingFields.CreditNote || []).concat(
          monetizeNowFields.CreditNote || [],
        ),
      },
    };
  }

  if (
    'Invoice' in accountingFields &&
    ('Invoice' in monetizeNowFields || 'Invoice' in monetizeNowAccountingFields)
  ) {
    response = {
      ...response,
      invoiceToInvoice: {
        accounting: accountingFields.Invoice || [],
        monetize: (monetizeNowAccountingFields.Invoice || []).concat(
          monetizeNowFields.Invoice || [],
        ),
      },
    };
  }

  return response;
};

export const transformAccountingFields = (
  matchedFields: AccountingFieldMatch,
  objectType: 'accounting' | 'monetize',
): FieldMappingBaseFieldType[] => {
  return (matchedFields[objectType]
    ?.map(({ displayLabel, fieldApiName, dataType }) => ({
      displayLabel: stringifyDisplayLabel(displayLabel),
      fieldApiName,
      dataType,
    }))
    .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel)) ??
    []) satisfies FieldMappingBaseFieldType[];
};

export const transformAccToFieldMappingBatchUpsertPayloadType = (
  targetSystem: FieldMappingSystemType,
  sourceEntity: FieldMappingObjectType,
  targetEntity: FieldMappingObjectType,
  payloads: FieldMappingCreateReqType[],
): FieldMappingBatchUpsertPayloadType => {
  return payloads.reduce(
    (result, field) => {
      const mappingField = {
        source: {
          fieldApiName: field.source.fieldApiName,
        },
        target: {
          fieldApiName: field.target.fieldApiName,
        },
        mode: field.mode,
      } as FieldMappingBatchUpsertPayloadMappingFieldType;

      result.mappings.push(mappingField);
      return result;
    },
    {
      sourceSystem: 'monetizenow',
      sourceEntity,
      targetSystem,
      targetEntity,
      mappings: [],
    } as FieldMappingBatchUpsertPayloadType,
  );
};

export const mapExistingFieldMappingFromMn = (
  data?: FieldMappingsResType,
  accountingFields?: FieldMappingType,
  monetizeNowFields?: FieldMappingType,
) => {
  const response = {
    userDefinedCreditNotesToCreditMemo: [] as FieldMappingCreateReqType[],
    userDefinedInvoiceToInvoice: [] as FieldMappingCreateReqType[],
  };
  if (!data) {
    return response;
  }

  const displayLabelLookupRecord = transformToDisplayLabelLookupRecord(
    accountingFields,
    monetizeNowFields,
  );

  return data.fromMn.reduce((result, field) => {
    const convertToFieldResult = convertToField(
      field,
      displayLabelLookupRecord,
    );

    if (
      field.source.objectType === 'CreditNote' &&
      field.target.objectType === 'CreditMemo'
    ) {
      result.userDefinedCreditNotesToCreditMemo.push(convertToFieldResult);
    }
    if (
      field.source.objectType === 'Invoice' &&
      field.target.objectType === 'Invoice'
    ) {
      result.userDefinedInvoiceToInvoice.push(convertToFieldResult);
    }
    return result;
  }, response);
};
